import React, { useState } from "react";
import constant from "../../../../constants";
import { FormInput, FormSelect, SectionTitle } from "./FormField";
import { CopyButton } from "./CopyButton";
import { ShippingLabelPrintModal } from "./ShippingLabelPrintModal";
import { FaExternalLinkAlt, FaPrint } from "react-icons/fa";
import { DatePicker } from "antd";
import th from "antd/es/date-picker/locale/th_TH";
import dayTh from "dayjs/locale/th";
import dayjs from "dayjs";
import buddhistEra from "dayjs/plugin/buddhistEra";

dayjs.extend(buddhistEra);
dayjs.locale(dayTh);

const buddhistLocale = {
  ...th,
  lang: {
    ...th.lang,
    fieldDateFormat: "DD-MM-BBBB",
    fieldDateTimeFormat: "DD-MM-BBBB HH:mm:ss",
    yearFormat: "BBBB",
    cellYearFormat: "BBBB",
  },
};

export const OrderShipping = ({
  orderInfo,
  setOrderInfo,
  updateValue,
  loading,
}) => {
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);

  if (orderInfo?.deliverChannel !== "mail") return null;

  const fullAddress = `${orderInfo?.address?.addr || ""} ${orderInfo?.address?.province || ""} ${orderInfo?.address?.zipcode || ""}`.trim();

  const handleOpenTrackingUrl = () => {
    if (!orderInfo?.shippingTrack) return;
    const trackNo = encodeURIComponent(orderInfo.shippingTrack.trim());
    window.open(`https://track.thailandpost.co.th/?trackNumber=${trackNo}`, "_blank");
  };

  return (
    <div>
      <div className="flex items-center justify-between mt-6 mb-3 border-b border-slate-200 pb-2">
        <h3 className="text-lg font-bold text-slate-800">การจัดส่งไปรษณีย์</h3>

        <div className="flex items-center gap-2">
          {Boolean(orderInfo?.shippingTrack) && (
            <button
              type="button"
              onClick={handleOpenTrackingUrl}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors shadow-xs cursor-pointer"
              title="ตรวจสอบสถานะพัสดุบนเว็บไปรษณีย์ไทย"
            >
              <FaExternalLinkAlt className="text-[10px]" />
              <span>เช็คเลขพัสดุ</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => setIsPrintModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold text-slate-700 bg-white hover:bg-slate-50 border border-slate-300 rounded-md transition-colors shadow-xs cursor-pointer"
            title="พิมพ์ใบปะหน้าพัสดุ"
          >
            <FaPrint className="text-slate-500" />
            <span>พิมพ์ใบปะหน้า</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* สถานะการจัดส่ง */}
        <FormSelect
          label="สถานะการจัดส่ง"
          value={orderInfo?.shippingStatus || ""}
          disabled={loading}
          onChange={(e) => updateValue("shippingStatus", e.target.value)}
        >
          <option value="">-</option>
          <option value={constant.shippingStatuses.PENDING}>
            {constant.shippingStatusesTranslate.PENDING}
          </option>
          <option value={constant.shippingStatuses.PACKING}>
            {constant.shippingStatusesTranslate.PACKING}
          </option>
          <option value={constant.shippingStatuses.SHIPPED}>
            {constant.shippingStatusesTranslate.SHIPPED}
          </option>
          <option value={constant.shippingStatuses.CANCEL}>
            {constant.shippingStatusesTranslate.CANCEL}
          </option>
        </FormSelect>

        {/* วันที่จัดส่ง */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-slate-600">
            วันที่จัดส่ง
          </label>
          <DatePicker
            className="w-full h-[38px] px-2.5 text-sm font-semibold border border-slate-300 rounded-md bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
            locale={buddhistLocale}
            disabled={loading}
            value={
              typeof orderInfo.shippingDate === "string"
                ? dayjs(orderInfo.shippingDate, "YYYY-MM-DD")
                : false
            }
            onChange={(_, th) => {
              if (!th) {
                updateValue("shippingDate", "");
                return;
              }
              const parts = th.split("-");
              if (parts.length === 3) {
                const year = Number(parts[2]) - 543;
                const month = parts[1];
                const day = parts[0];
                updateValue("shippingDate", `${year}-${month}-${day}`);
              }
            }}
          />
        </div>

        {/* เลขติดตามการจัดส่ง */}
        <FormInput
          label="เลขติดตามการจัดส่ง"
          disabled={loading}
          value={orderInfo?.shippingTrack || ""}
          suffix={<CopyButton text={orderInfo?.shippingTrack} />}
          onChange={(e) =>
            setOrderInfo((prev) => ({
              ...prev,
              shippingTrack: e.target.value,
            }))
          }
          onBlur={(e) => updateValue("shippingTrack", e.target.value)}
        />

        {/* ชื่อผู้รับ */}
        <FormInput
          label="ชื่อผู้รับ"
          disabled={loading}
          value={orderInfo?.firstName || ""}
          onChange={(e) =>
            setOrderInfo((prev) => ({ ...prev, firstName: e.target.value }))
          }
          onBlur={(e) => updateValue("firstName", e.target.value)}
        />

        {/* นามสกุลผู้รับ */}
        <FormInput
          label="นามสกุลผู้รับ"
          disabled={loading}
          value={orderInfo?.lastName || ""}
          onChange={(e) =>
            setOrderInfo((prev) => ({ ...prev, lastName: e.target.value }))
          }
          onBlur={(e) => updateValue("lastName", e.target.value)}
        />

        {/* เบอร์โทรศัพท์ผู้รับ */}
        <FormInput
          label="เบอร์โทรศัพท์ผู้รับ"
          disabled={loading}
          value={orderInfo?.phone || ""}
          suffix={<CopyButton text={orderInfo?.phone} />}
          onChange={(e) =>
            setOrderInfo((prev) => ({ ...prev, phone: e.target.value }))
          }
          onBlur={(e) => updateValue("phone", e.target.value)}
        />

        {/* ที่อยู่จัดส่ง */}
        <FormInput
          label="ที่อยู่จัดส่ง"
          disabled={loading}
          value={orderInfo?.address?.addr || ""}
          suffix={<CopyButton text={fullAddress} title="คัดลอกที่อยู่จัดส่งทั้งหมด" />}
          onChange={(e) =>
            setOrderInfo((prev) => ({
              ...prev,
              address: { ...prev.address, addr: e.target.value },
            }))
          }
          onBlur={(e) => updateValue("address.addr", e.target.value)}
        />

        {/* จังหวัด */}
        <FormSelect
          label="จังหวัด"
          disabled={loading}
          value={orderInfo?.address?.province || ""}
          onChange={(e) => updateValue("address.province", e.target.value)}
        >
          <option value="">-</option>
          {constant.provinces.map((province) => (
            <option key={province} value={province}>
              {province}
            </option>
          ))}
        </FormSelect>

        {/* รหัสไปรษณีย์ */}
        <FormInput
          label="รหัสไปรษณีย์"
          disabled={loading}
          value={orderInfo?.address?.zipcode || ""}
          onChange={(e) =>
            setOrderInfo((prev) => ({
              ...prev,
              address: { ...prev.address, zipcode: e.target.value },
            }))
          }
          onBlur={(e) => updateValue("address.zipcode", e.target.value)}
        />
      </div>

      {/* Modal พิมพ์ใบปะหน้าพัสดุ */}
      <ShippingLabelPrintModal
        isOpen={isPrintModalOpen}
        orderInfo={orderInfo}
        onClose={() => setIsPrintModalOpen(false)}
      />
    </div>
  );
};
